/**
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business Spec Console web ↔ API の通信に使う oRPC 型付きクライアント。共有 contract
 *   （@cortex/domain-spec-console）から型を導出するため、エンドポイント URL や手書きの
 *   レスポンス型を介さず、API の入出力型がそのままクライアントに伝播する。cookie セッション認証のため
 *   credentials を include する。
 * @graph-connects /rpc [calls] Spec Console oRPC endpoint
 */
import { specConsoleContract } from '@cortex/domain-spec-console';
import { createORPCClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import type { ContractRouterClient } from '@orpc/contract';
import { config } from '../config.js';

/**
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business API のオリジン指定（絶対 URL）と Vite プロキシ経由（相対パス）の双方から oRPC エンドポイント URL を解決する。
 * @graph-connects globalThis.location [references, via:origin] 相対 API URL の解決に使うブラウザオリジン
 */
export function resolveRpcUrl(apiUrl: string, origin: string = globalThis.location.origin): string {
  const normalizedApiUrl = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
  return new URL('rpc', new URL(normalizedApiUrl, origin)).toString();
}

/**
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business oRPC のリクエストを SSO cookie 付きで送出する。cookie セッション認証のため credentials を include する。
 * @graph-connects /rpc [calls] Spec Console oRPC endpoint
 */
export function fetchWithCredentials(request: Request, init?: RequestInit): Promise<Response> {
  return globalThis.fetch(request, { ...init, credentials: 'include' });
}

/** @graph-connects /rpc [calls] oRPC RPC-protocol link */
const link = new RPCLink({
  url: resolveRpcUrl(config.apiUrl),
  fetch: fetchWithCredentials,
});

/**
 * Spec Console API の型付き oRPC クライアント。`rpcClient.auth.me()` のように
 * feature.procedure で呼び出す。
 * @graph-connects /rpc [calls] Spec Console oRPC procedures
 */
export const rpcClient: ContractRouterClient<typeof specConsoleContract> = createORPCClient(link);
